import type { ProfileData } from "@recursive-robot/react-jsx-parser";

/**
 * All profiling batches produced by a single React render pass share the same `cycleId`.
 * A cycle is assembled incrementally: the synchronous `render` batch arrives first, then any
 * lazily-invoked `callback` batches, then (if React-render profiling is on) the `react` batch
 * on a post-commit microtask.  Never treat a cycle as "complete" on first sight.
 */
export type CycleEntry = {
    cycleId: string;
    // The enclosing parser's cycleId; null for the page-root Canvas parser. Used to correlate the
    // Canvas + nested Template renders of one pass into a group (see `groupId`).
    parentCycleId: string | null;
    // The resolved root cycleId (topmost ancestor via `parentCycleId`); identifies the group.
    groupId: string;
    // The full/original file name (for navigating to/opening the template).
    filePath?: string;
    // The shortened file name (for display purposes).
    fileName?: string;
    // `performance.now()`-based clock value when the cycle's first batch began.
    startTime: number;
    render?: ProfileData;
    callbacks: ProfileData[];
    react?: ProfileData;
};

export type ProfilingState = {
    // Presence of these flags gates the parser's `onProfile` / `profileReactRender` props.
    enabled: boolean;
    reactRenderEnabled: boolean;
    // Root cycleIds in arrival order (oldest -> newest); acts as the group ring-buffer index.
    groupOrder: string[];
    // Root cycleId -> its member cycleIds (arrival order within the group).
    groupCycleIds: Record<string, string[]>;
    cyclesById: Record<string, CycleEntry>;
    // Floor (performance.now() clock) for pass<->event attribution: the oldest pass's causal window
    // opens here instead of -Infinity, so events preceding a mid-session enable or a Clear are not
    // cited as its triggers.  Deliberately null when the enabled flag is restored from localStorage
    // at startup — boot-time events (initial URL/parameter/frame-data traces) genuinely cause the
    // first pass and must stay attributable.  Stamped only by the enable action (with an empty ring
    // buffer) and by Clear.
    startedAtPerf: number | null;
    selectedCycleId: string | null;
    // Retained-group cap (ring buffer); oldest groups (with all their cycles) are evicted beyond this.
    maxGroups: number;
};

export const PROFILING_SET_ENABLED = "PROFILING_SET_ENABLED";
export const PROFILING_SET_REACT_RENDER_ENABLED = "PROFILING_SET_REACT_RENDER_ENABLED";
export const PROFILING_BATCH_RECEIVED = "PROFILING_BATCH_RECEIVED";
export const PROFILING_SELECT_CYCLE = "PROFILING_SELECT_CYCLE";
export const PROFILING_CLEAR = "PROFILING_CLEAR";

export interface SetProfilingEnabledAction {
    type: typeof PROFILING_SET_ENABLED;
    enabled: boolean;
    // performance.now() at dispatch — read in the action creator to keep the reducer pure.
    atPerf: number;
}

export interface SetReactRenderEnabledAction {
    type: typeof PROFILING_SET_REACT_RENDER_ENABLED;
    enabled: boolean;
}

export interface ProfilingBatchReceivedAction {
    type: typeof PROFILING_BATCH_RECEIVED;
    batch: ProfileData;
}

export interface SelectCycleAction {
    type: typeof PROFILING_SELECT_CYCLE;
    cycleId: string | null;
}

export interface ClearProfilingAction {
    type: typeof PROFILING_CLEAR;
    // performance.now() at dispatch — read in the action creator to keep the reducer pure.
    atPerf: number;
}

export type ProfilingActionTypes =
    | SetProfilingEnabledAction
    | SetReactRenderEnabledAction
    | ProfilingBatchReceivedAction
    | SelectCycleAction
    | ClearProfilingAction;
